import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../stores';
import { FamlaServices } from '../../services';
import { iAlert } from '../../interfaces';
import { InputForm, Spiner } from '../../components';
import { isPrivateEmail } from '../../utils/emailUtils';

interface AddProcessOwnerModalProps {
    processId: string;
    modalId: string;
}

const AddProcessOwnerModal: React.FC<AddProcessOwnerModalProps> = ({ processId, modalId }) => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const [email, setEmail] = useState('');
    const [isSubmit, setIsSubmit] = useState(false);
    const [alert, setAlert] = useState<iAlert>({ isActive: false, type: '', message: '' });

    const handleSubmit = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.enterValideEmail') });
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.enterValideEmail') });
            return;
        }
        if (isPrivateEmail(trimmed)) {
            setAlert({ isActive: true, type: 'warning', message: t('views.auth.professionalEmail') });
            return;
        }
        setIsSubmit(true);
        setAlert({ isActive: false, type: '', message: '' });
        const data = await FamlaServices(
            'api_host',
            '/process/add_co_owner/',
            'PUT',
            { process_id: processId, mail: [trimmed.toLowerCase()] },
            users.access_token
        );
        if (data.status === 200) {
            setAlert({ isActive: true, type: 'success', message: t('navigation.invitationSentSuccessfully') });
            setEmail('');
        } else if (data.status === 400) {
            const d = (data as { body?: { description?: string } }).body?.description;
            setAlert({
                isActive: true,
                type: 'danger',
                message: d || t('navigation.anErrorOccurred'),
            });
        } else {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.anErrorOcured') });
        }
        setIsSubmit(false);
    };

    return (
        <div className="modal fade" id={modalId} tabIndex={-1} aria-hidden="true" data-bs-backdrop="static">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content bg-dark">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">
                            {t('views.processView.addProcessOwner')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close bg-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        />
                    </div>
                    <div className="modal-body">
                        {alert.isActive && (
                            <div className={`alert alert-${alert.type}`} role="alert">
                                {alert.message}
                            </div>
                        )}
                        <p className="text-light small text-opacity-75 mb-2">
                            {t('views.processView.addProcessOwnerHint')}
                        </p>
                        <p className="text-light small text-opacity-60 mb-3" style={{ fontSize: 12, lineHeight: 1.45 }}>
                            {t('views.processView.addProcessOwnerScenarios')}
                        </p>
                        <InputForm
                            inputType="email"
                            inputLabel={t('words.email')}
                            inputName="add-co-owner-email"
                            inputValue={email}
                            isFloatingLabel={true}
                            labelClass="text-theme"
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setEmail(ev.target.value)}
                            inputContainerClass="mb-0 p-0"
                            inputClass="bg-dark text-light text-bg-dark"
                            inputStyle={{ height: 0, padding: 0 }}
                        />
                    </div>
                    <div className="modal-footer border-0">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                            {t('words.close')}
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="btn btn-primary"
                            disabled={isSubmit}
                        >
                            {isSubmit ? <Spiner className="spinner-border-sm" /> : t('words.invite')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProcessOwnerModal;
